import api from '../../../services/api';
import { createWrapperPayload, wrapperConstants } from '../../../services/wrapper-api';

export interface FormData {
  liquidFlowrate: string | number | null;
  waterCut: string | number | null;
  totalGasOilRatio: string | number | null;
  wellheadPressure: string | number | null;
  wellheadTemperature: string | number | null;
  casingPressure: string | number | null;
  flowlinePressure: string | number | null;
  productionChokePosition: string | number | null;
  pumpIntakePressure: string | number | null;
  pumpIntakeTemperature: string | number | null;
  pumpFrequency: string | number | null;
  pumpDischargePressure: string | number | null;
  waterDensity: string | number | null;
  comment: string;
  enabled: boolean;
  wellId: number | null;
  methodOfProduction?: string | null;
  date?: string;
  id?: number | null;
  oil?: string | number | null;
  water?: string | number | null;
  gas?: string | number | null;
  downholePressure?: string | null;
  downholeTemperature?: string | null;
  resultsPwf?: string | null;
  productivityIndex?: string | null;
  injectionRate?: string | null;
  flowPath?: number;
  annulusChokeSettings?: string | null;
  hasStandardDeviation?: boolean;
  hasTestWarnings?: boolean;
  isQuestionable?: boolean;
  reservoirPressure?: number | string;
}

interface WellTestData {
  Id: number;
  WellId: number;
  MethodOfProduction: string;
  Date: string;
  Enabled: boolean;
  Comment: string;
  WellheadPressure: number;
  WellheadTemperature: number;
  CasingPressure?: number | null;
  FlowlinePressure?: number | null;
  ProductionChokePosition?: number | null;
  WaterDensity: number;
  HasStandardDeviation: boolean;
  HasTestWarnings: boolean;
  IsQuestionable: boolean;
  LiquidFlowrate: number;
  WaterCut: number;
  TotalGasOilRatio: number;
  PumpIntakePressure: number;
  PumpIntakeTemperature: number;
  PumpFrequency: number;
  PumpDischargePressure?: number | null;
  FlowPath?: number;
  Oil?: number;
  Water?: number;
  Gas?: number;
  DownholePressure?: number | null;
  DownholeTemperature?: number | null;
  ResultsPwf?: number | null;
  ProductivityIndex?: number | null;
  InjectionRate?: number | null;
  AnnulusChokeSettings?: string | null;
  ReservoirPressure: number | null;
}

export const transformFormData = (formData: FormData): WellTestData => {
  const transformedData: WellTestData = {
    Id: formData.id ?? 0,
    WellId: formData.wellId ?? 13,
    MethodOfProduction: formData.methodOfProduction ?? 'ESP',
    Date: formData.date ?? '',
    Enabled: formData.enabled,
    Comment: formData.comment,
    WellheadPressure: formData.wellheadPressure ? Number(formData.wellheadPressure) : 0,
    WellheadTemperature: formData.wellheadTemperature ? Number(formData.wellheadTemperature) : 0,
    CasingPressure: formData.casingPressure ? Number(formData.casingPressure) : null,
    FlowlinePressure: formData.flowlinePressure ? Number(formData.flowlinePressure) : null,
    ProductionChokePosition: formData.productionChokePosition ? Number(formData.productionChokePosition) : null,
    WaterDensity: formData.waterDensity ? Number(formData.waterDensity) : 0,
    HasStandardDeviation: formData.hasStandardDeviation ?? false,
    HasTestWarnings: formData.hasTestWarnings ?? false,
    IsQuestionable: formData.isQuestionable ?? false,
    LiquidFlowrate: formData.liquidFlowrate ? Number(formData.liquidFlowrate) : 0,
    WaterCut: formData.waterCut ? Number(formData.waterCut) : 0,
    TotalGasOilRatio: formData.totalGasOilRatio ? Number(formData.totalGasOilRatio) : 0,
    PumpIntakePressure: formData.pumpIntakePressure ? Number(formData.pumpIntakePressure) : 0,
    PumpIntakeTemperature: formData.pumpIntakeTemperature ? Number(formData.pumpIntakeTemperature) : 0,
    PumpFrequency: formData.pumpFrequency ? Number(formData.pumpFrequency) : 0,
    PumpDischargePressure: formData.pumpDischargePressure ? Number(formData.pumpDischargePressure) : null,
    FlowPath: formData.flowPath ?? 0,
    Oil: formData.oil ? Number(formData.oil) : 0,
    Water: formData.water ? Number(formData.water) : 0,
    Gas: formData.gas ? Number(formData.gas) : 0,
    DownholePressure: formData.downholePressure ? Number(formData.downholePressure) : null,
    DownholeTemperature: formData.downholeTemperature ? Number(formData.downholeTemperature) : null,
    ResultsPwf: formData.resultsPwf ? Number(formData.resultsPwf) : null,
    ProductivityIndex: formData.productivityIndex ? Number(formData.productivityIndex) : null,
    InjectionRate: formData.injectionRate ? Number(formData.injectionRate) : null,
    AnnulusChokeSettings: formData.annulusChokeSettings ?? null,
    ReservoirPressure: formData.reservoirPressure ? Number(formData.reservoirPressure) : null,
  };

  // Remove fields with empty or null values
  return Object.fromEntries(
    Object.entries(transformedData).filter(
      ([key, value]) => key === 'Id' || (value !== '' && value !== null && value !== 0),
    ),
  ) as WellTestData;
};

export const saveWellTest = async (formData: FormData): Promise<string> => {
  try {
    const transformedData = transformFormData(formData); // Transform the formData

    const requestPayload = createWrapperPayload('POST', '/pumpchecker/api/v1/tests', transformedData);

    const response = await api.post(wrapperConstants.router, requestPayload);
    return response.data;
  } catch (error) {
    console.error('Error saving well test:', error);
    return '';
  }
};
